/* eslint-disable */
import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Clock, AlertTriangle, CreditCard, FileText, Receipt } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const FLUTTERWAVE_PUBLIC_KEY = process.env.REACT_APP_FLUTTERWAVE_PUBLIC_KEY;

const QuotePaymentPage = () => {
  const { quoteId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [quoteData, setQuoteData] = useState(null);
  const [pageStatus, setPageStatus] = useState('loading'); // loading, awaiting_payment, already_paid, expired, cancelled, error
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [message, setMessage] = useState('');

  // Helper function to get currency symbol
  const getCurrencySymbol = () => {
    return quoteData?.currency === 'USD' ? '$' : '₦';
  };

  // Helper function to format price based on quote currency
  const formatPrice = (amount) => {
    const symbol = getCurrencySymbol();
    if (quoteData?.currency === 'USD') {
      // For USD, use converted amounts or convert using stored exchange rate
      const exchangeRate = quoteData?.exchange_rate_used || 1;
      const usdAmount = amount / exchangeRate;
      return `${symbol}${usdAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return `${symbol}${amount?.toLocaleString()}`;
  };

  // Get display total based on currency
  const getDisplayTotal = () => {
    if (quoteData?.currency === 'USD') {
      return quoteData?.converted_total || 0;
    }
    return quoteData?.original_ngn_total || quoteData?.total || 0;
  };

  // Format total with proper currency
  const formatTotal = () => {
    const symbol = getCurrencySymbol();
    const total = getDisplayTotal();
    if (quoteData?.currency === 'USD') {
      return `${symbol}${total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return `${symbol}${total?.toLocaleString()}`;
  };

  useEffect(() => {
    if (!quoteId || !token) {
      setPageStatus('error');
      setMessage('Invalid payment link. Please check the link or contact support.');
      setLoading(false);
      return;
    }
    
    fetchQuoteDetails();
  }, [quoteId, token]);

  const fetchQuoteDetails = async () => {
    try {
      const response = await fetch(`${API_URL}/api/quote/pay/${quoteId}?token=${token}`);
      const data = await response.json();
      
      if (!response.ok) {
        setPageStatus('error');
        setMessage(data.detail || 'Failed to load quote details');
        return;
      }
      
      setPageStatus(data.status);
      setQuoteData(data.quote);
      setMessage(data.message || '');
    } catch (error) {
      setPageStatus('error');
      setMessage('Failed to connect to payment server. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handlePayNow = async () => {
    if (!quoteData) return;
    
    setPaymentLoading(true);
    try {
      // Initialize payment
      const response = await fetch(`${API_URL}/api/quote/pay/${quoteId}/initialize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });
      
      const data = await response.json();
      
      if (!response.ok || !data.status) {
        throw new Error(data.detail || 'Failed to initialize payment');
      }
      
      const { tx_ref, amount, link, is_mock } = data.data;
      
      // If there's a Flutterwave link, redirect to it
      if (link) {
        // Try opening in same window first
        const newWindow = window.open(link, '_self');
        if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
          // Fallback: direct navigation
          window.location.href = link;
        }
        return;
      }
      
      // If mock mode or no link, use Flutterwave inline
      if (typeof window.FlutterwaveCheckout === 'function') {
        window.FlutterwaveCheckout({
          public_key: FLUTTERWAVE_PUBLIC_KEY,
          tx_ref: tx_ref,
          amount: amount,
          currency: 'NGN',
          payment_options: 'card,mobilemoney,ussd,banktransfer',
          customer: {
            email: quoteData.client_email,
            phone_number: quoteData.client_phone || '',
            name: quoteData.client_name
          },
          customizations: {
            title: 'TEMARUCO Quote Payment',
            description: `Payment for Quote #${quoteData.quote_number}`,
            logo: `${API_URL}/logo.png`
          },
          callback: async function (response) {
            // Verify payment
            await verifyPayment(response.transaction_id, tx_ref);
          },
          onclose: function () {
            setPaymentLoading(false);
          }
        });
      } else {
        // Fallback for mock mode
        if (is_mock) {
          await verifyPayment('mock_txn_' + Date.now(), tx_ref);
        } else {
          throw new Error('Payment gateway not available');
        }
      }
    } catch (error) {
      toast.error(error.message || 'Payment initialization failed');
      setPaymentLoading(false);
    }
  };

  const verifyPayment = async (transactionId, txRef) => {
    try {
      const response = await fetch(`${API_URL}/api/quote/pay/${quoteId}/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          transaction_id: transactionId,
          tx_ref: txRef
        })
      });
      
      const data = await response.json();
      
      if (data.status && (data.data.status === 'successful' || data.data.status === 'already_paid')) {
        setPageStatus('payment_success');
        toast.success('Payment successful! Receipt has been sent to your email.');
      } else {
        toast.error('Payment verification failed. Please contact support.');
      }
    } catch (error) {
      toast.error('Payment verification failed. Please contact support.');
    } finally {
      setPaymentLoading(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent mx-auto mb-4"></div>
          <p className="text-zinc-600">Loading quote details...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (pageStatus === 'error') {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <XCircle className="mx-auto text-red-500 mb-4" size={64} />
          <h1 className="text-2xl font-bold text-zinc-900 mb-4">Payment Error</h1>
          <p className="text-zinc-600 mb-6">{message}</p>
          <button
            onClick={() => window.location.href = 'mailto:temarucoltd@gmail.com'}
            className="bg-primary text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary/90 transition"
          >
            Contact Support
          </button>
        </div>
      </div>
    );
  }

  // Already paid state
  if (pageStatus === 'already_paid') {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <CheckCircle className="mx-auto text-green-500 mb-4" size={64} />
          <h1 className="text-2xl font-bold text-green-600 mb-4">Already Paid</h1>
          <p className="text-zinc-600 mb-6">{message}</p>
          {quoteData && (
            <div className="bg-green-50 rounded-lg p-4 mb-6 text-left">
              <p className="text-sm text-zinc-500">Quote Number</p>
              <p className="font-mono font-semibold">{quoteData.quote_number}</p>
              <p className="text-sm text-zinc-500 mt-3">Paid Amount</p>
              <p className="text-2xl font-bold text-green-600">{formatTotal()}</p>
              {quoteData.payment_reference && (
                <>
                  <p className="text-sm text-zinc-500 mt-3">Payment Reference</p>
                  <p className="font-mono text-sm">{quoteData.payment_reference}</p>
                </>
              )}
            </div>
          )}
          <p className="text-sm text-zinc-500">A receipt has been sent to your email.</p>
        </div>
      </div>
    );
  }

  // Payment success state (after successful payment on this page)
  if (pageStatus === 'payment_success') {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="text-green-600" size={48} />
          </div>
          <h1 className="text-3xl font-bold text-green-600 mb-4">Payment Successful!</h1>
          <p className="text-zinc-600 mb-6">
            Thank you for your payment. Your order is now being processed.
          </p>
          {quoteData && (
            <div className="bg-green-50 rounded-lg p-4 mb-6 text-left">
              <p className="text-sm text-zinc-500">Quote Number</p>
              <p className="font-mono font-semibold">{quoteData.quote_number}</p>
              <p className="text-sm text-zinc-500 mt-3">Amount Paid</p>
              <p className="text-2xl font-bold text-green-600">{formatTotal()}</p>
            </div>
          )}
          <div className="flex items-center justify-center gap-2 text-sm text-zinc-500">
            <Receipt size={16} />
            <span>A receipt has been sent to your email</span>
          </div>
        </div>
      </div>
    );
  }

  // Expired state
  if (pageStatus === 'expired') {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <Clock className="mx-auto text-orange-500 mb-4" size={64} />
          <h1 className="text-2xl font-bold text-orange-600 mb-4">Quote Expired</h1>
          <p className="text-zinc-600 mb-6">{message}</p>
          {quoteData && (
            <div className="bg-orange-50 rounded-lg p-4 mb-6 text-left">
              <p className="text-sm text-zinc-500">Quote Number</p>
              <p className="font-mono font-semibold">{quoteData.quote_number}</p>
            </div>
          )}
          <button
            onClick={() => window.location.href = 'mailto:temarucoltd@gmail.com?subject=Request New Quote'}
            className="bg-primary text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary/90 transition"
          >
            Request New Quote
          </button>
        </div>
      </div>
    );
  }

  // Cancelled state
  if (pageStatus === 'cancelled') {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <AlertTriangle className="mx-auto text-red-500 mb-4" size={64} />
          <h1 className="text-2xl font-bold text-red-600 mb-4">Quote Cancelled</h1>
          <p className="text-zinc-600 mb-6">{message}</p>
          <button
            onClick={() => window.location.href = 'mailto:temarucoltd@gmail.com'}
            className="bg-primary text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary/90 transition"
          >
            Contact Support
          </button>
        </div>
      </div>
    );
  }

  // Awaiting payment state (main payment page)
  return (
    <div className="min-h-screen bg-zinc-50 py-8 px-4">
      {/* Header */}
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-primary mb-2">TEMARUCO</h1>
          <p className="text-sm text-zinc-500 tracking-widest">CLOTHING FACTORY</p>
        </div>

        {/* Quote Card */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Quote Header */}
          <div className="bg-gradient-to-r from-primary to-primary/80 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 text-white">
                <FileText size={24} />
                <div>
                  <p className="text-sm opacity-90">Quote Payment</p>
                  <p className="font-bold">{quoteData?.quote_number}</p>
                </div>
              </div>
              {quoteData?.expires_at && (
                <div className="text-white text-right text-sm">
                  <p className="opacity-80">Valid until</p>
                  <p className="font-semibold">
                    {new Date(quoteData.expires_at).toLocaleDateString('en-NG', {
                      month: 'short', day: 'numeric', year: 'numeric'
                    })}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Quote Details */}
          <div className="p-6">
            {/* Client Info */}
            <div className="mb-6 pb-6 border-b">
              <p className="text-sm text-zinc-500 mb-1">Bill To</p>
              <p className="font-semibold text-lg">{quoteData?.client_name}</p>
              <p className="text-zinc-600">{quoteData?.client_email}</p>
              {quoteData?.client_phone && (
                <p className="text-zinc-600">{quoteData.client_phone}</p>
              )}
            </div>

            {/* Items List - Mobile Optimized */}
            <div className="mb-6">
              <h3 className="font-semibold text-zinc-900 mb-3">Order Items</h3>
              <div className="space-y-3">
                {quoteData?.items?.map((item, index) => (
                  <div key={index} className="bg-zinc-50 rounded-lg p-3">
                    <p className="font-medium text-zinc-900 text-sm mb-2">{item.description}</p>
                    <div className="flex justify-between text-xs text-zinc-600">
                      <span>Qty: {item.quantity}</span>
                      <span>@ {formatPrice(item.unit_price)}</span>
                      <span className="font-semibold text-zinc-900">{formatPrice(item.total)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Totals */}
            <div className="bg-zinc-50 rounded-lg p-4 mb-6">
              <div className="flex justify-between mb-2">
                <span className="text-zinc-600">Subtotal</span>
                <span className="font-medium">{formatPrice(quoteData?.subtotal)}</span>
              </div>
              {quoteData?.tax > 0 && (
                <div className="flex justify-between mb-2">
                  <span className="text-zinc-600">Tax</span>
                  <span>{formatPrice(quoteData.tax)}</span>
                </div>
              )}
              {quoteData?.discount > 0 && (
                <div className="flex justify-between mb-2 text-green-600">
                  <span>Discount</span>
                  <span>-{formatPrice(quoteData.discount)}</span>
                </div>
              )}
              <div className="flex justify-between pt-3 border-t border-zinc-300">
                <span className="text-lg font-bold">Total</span>
                <span className="text-2xl font-bold text-primary">{formatTotal()}</span>
              </div>
            </div>

            {/* Notes */}
            {quoteData?.notes && (
              <div className="bg-zinc-50 rounded-lg p-4 mb-6">
                <p className="text-sm text-zinc-500 mb-1">Notes</p>
                <p className="text-zinc-700 whitespace-pre-wrap">{quoteData.notes}</p>
              </div>
            )}

            {/* Pay Now Button */}
            <button
              onClick={handlePayNow}
              disabled={paymentLoading}
              className="w-full bg-gradient-to-r from-primary to-primary/90 text-white py-4 rounded-lg font-bold text-lg flex items-center justify-center gap-3 hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="pay-now-btn"
            >
              {paymentLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard size={24} />
                  Pay {formatTotal()} Now
                </>
              )}
            </button>

            {/* Payment Methods */}
            <div className="mt-4 text-center">
              <p className="text-xs text-zinc-500 mb-2">Secure payment via Flutterwave</p>
              <div className="flex items-center justify-center gap-4 text-zinc-400">
                <span className="text-xs">Card</span>
                <span className="text-xs">•</span>
                <span className="text-xs">Bank Transfer</span>
                <span className="text-xs">•</span>
                <span className="text-xs">Mobile Money</span>
                <span className="text-xs">•</span>
                <span className="text-xs">USSD</span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-zinc-500">
          <p>Questions? Contact us at <a href="mailto:temarucoltd@gmail.com" className="text-primary hover:underline">temarucoltd@gmail.com</a></p>
          <p className="mt-2">© {new Date().getFullYear()} Temaruco Clothing Factory. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default QuotePaymentPage;
